import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, faCheckCircle, faFilter,
  faArrowRight, faCalendar, faEye, faAtom,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { discoveriesData, categories, impactLevels } from './DiscoveriesData';
import DiscoveryModal from './DiscoveryModal';
import './OmicsDiscoveries.css';

const DiscoveryCard = ({ discovery, size = 'standard', onClick }) => {
  const categoryInfo = categories.find(cat => cat.id === discovery.category);
  const isHighImpact = discovery.impact === 'high';
  
  return (
    <article 
      className={`omics-discoveries__card omics-discoveries__card--${size}`}
      onClick={() => onClick(discovery)}
      style={{ 
        animationDelay: `${Math.random() * 0.3}s`
      }}
    >
      <div className="omics-discoveries__card-header">
    <div style={{ display: "flex" }}>
                {discovery.verified && (
            <span className="omics-discoveries__verified-badge">
              <FontAwesomeIcon icon={faCheckCircle} />
            </span>
          )}
        <div className="omics-discoveries__category-badge">
          <FontAwesomeIcon icon={categoryInfo?.icon || faAtom} />
          <span>{discovery.category}</span>
        </div>
        </div>
        <div className="omics-discoveries__meta-badges">
              {isHighImpact && (
            <span className="omics-discoveries__high-impact-badge">
              High Impact
            </span>
          )}
  
      
        </div>
      </div>

      <div className="omics-discoveries__card-content">
        <h3 className="omics-discoveries__title">{discovery.title}</h3>
        <p className="omics-discoveries__preview">{discovery.preview}</p>
        
        {(size === 'large' || isHighImpact) && (
          <div className="omics-discoveries__quick-metrics">
            {Object.entries(discovery.metrics).slice(0, 2).map(([key, value]) => (
              <div key={key} className="omics-discoveries__quick-metric">
                <span className="omics-discoveries__metric-value">{value}</span>
                <span className="omics-discoveries__metric-label">{key}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="omics-discoveries__card-footer">
        <div className="omics-discoveries__footer-left">
          <span className="omics-discoveries__date">
            <FontAwesomeIcon icon={faCalendar} />
            {new Date(discovery.publishDate).toLocaleDateString()}
          </span>
          <span className="omics-discoveries__read-time">
            <FontAwesomeIcon icon={faEye} />
            {discovery.readTime}
          </span>
        </div>
        <div className="omics-discoveries__read-more">
          <FontAwesomeIcon icon={faChevronRight} />
        </div>
      </div>

    </article>
  );
};



const OmicsDiscoveries = ({ theme }) => {
  const [selectedDiscovery, setSelectedDiscovery] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  const handleDiscoveryClick = (discovery) => {
    setSelectedDiscovery(discovery);
    setTimeout(() => setModalVisible(true), 50);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setTimeout(() => setSelectedDiscovery(null), 300);
  };

  const filteredDiscoveries = useMemo(() => {
    let filtered = discoveriesData;
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(discovery => discovery.category === selectedCategory);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(discovery => 
        discovery.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        discovery.preview.toLowerCase().includes(searchTerm.toLowerCase()) ||
        discovery.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        discovery.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    return filtered;
  }, [searchTerm, selectedCategory]);

  const highImpactDiscoveries = filteredDiscoveries.filter(d => d.impact === 'high');
  const otherDiscoveries = filteredDiscoveries.filter(d => d.impact !== 'high');
  
  // Pagination: 6 items per page
  const itemsPerPage = 6;
  const totalPages = Math.ceil(otherDiscoveries.length / itemsPerPage);
  const currentDiscoveries = otherDiscoveries.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

  // Sort recent discoveries by date
  const recentDiscoveries = otherDiscoveries
    .filter(d => d.impact !== 'high')
    .sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate));

  return (
    <div className="omics-discoveries">
      {/* Title */}
      <h1 className="omics-discoveries__title">
        Omics Discoveries
      </h1>
      
      {/* Search and Filter Bar */}
      <div className="omics-discoveries__search-bar">
        <div className="omics-discoveries__search">
          <FontAwesomeIcon icon={faSearch} className="omics-discoveries__search-icon" />
          <input
            type="text"
            placeholder="Search discoveries..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="omics-discoveries__search-input"
          />
        </div>
        <button 
          className={`omics-discoveries__filter-toggle ${showFilters ? 'omics-discoveries__filter-toggle--active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <FontAwesomeIcon icon={faFilter} />
        </button>
      </div>

      {/* Filters */}
      <div className={`omics-discoveries__filters ${showFilters ? 'omics-discoveries__filters--visible' : ''}`}>
        {categories.map(category => (
          <button
            key={category.id}
            className={`omics-discoveries__filter-btn ${selectedCategory === category.id ? 'omics-discoveries__filter-btn--active' : ''}`}
            onClick={() => {
              setSelectedCategory(category.id);
              setCurrentPage(0);
            }}
          >
            <FontAwesomeIcon icon={category.icon} />
            <span>{category.name}</span>
          </button>
        ))}
      </div>

      {filteredDiscoveries.length === 0 ? (
        <div className="omics-discoveries__no-results">
          <FontAwesomeIcon icon={faSearch} />
          <h3>No discoveries found</h3>
          <p>Try adjusting your search or category filter</p>
        </div>
      ) : (
        <div className="omics-discoveries__content">
          {/* Left Column - High Impact */}
          <div className="omics-discoveries__left">
            <h2 className="omics-discoveries__section-title">
              High Impact
            </h2>
            <div className="omics-discoveries__high-impact-list">
              {highImpactDiscoveries.map(discovery => (
                <DiscoveryCard 
                  key={discovery.id}
                  discovery={discovery} 
                  size="large" 
                  onClick={handleDiscoveryClick}
                />
              ))}
            </div>
          </div>

          {/* Right Column - Recent */}
          <div className="omics-discoveries__right">
            <h2 className="omics-discoveries__section-title">
              Recent Findings
            </h2>
            <div className="omics-discoveries__recent-list">
              {recentDiscoveries.map(discovery => (
                <DiscoveryCard 
                  key={discovery.id}
                  discovery={discovery} 
                  size="standard" 
                  onClick={handleDiscoveryClick}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      <DiscoveryModal 
        discovery={selectedDiscovery} 
        onClose={handleCloseModal}
        isVisible={modalVisible}
      />
    </div>
  );
};

export default OmicsDiscoveries;